import { Box } from '@lemonade-hq/cdk';
import { MEDIA_QUERIES } from '@lemonade-hq/nuis-atomic';
import parse from 'html-react-parser';
import Image from 'next/image';
import type React from 'react';
import { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import type { Product } from '../defaults';
import { CUSTOM_URL, DYNAMIC_PRODUCT_PROPERTIES } from '../defaults';
import { CTA as Cta, PricePeriod, ProductBox, ProductSubtitle, ProductTitle } from './ProductsStyles';

const CardProductTitle = styled(ProductTitle)`
  margin-top: 6px;
`;

const CardProductSubtitle = styled(ProductSubtitle)`
  padding-top: 6px;
`;

const ImageDesktop = styled(Image)`
  display: none;
  ${MEDIA_QUERIES.tabletAndDesktop`
    display: block
  `}
`;

const ImageMobile = styled(Image)`
  display: block;
  ${MEDIA_QUERIES.tabletAndDesktop`
    display: none
  `}
`;

const CTAContainer = styled.span`
  margin-top: 10px;
  ${MEDIA_QUERIES.tabletAndDesktop`
    margin-top: 25px;
  `}
`;

const ButtonWrapper = styled(Box).attrs({
  as: 'span',
})``;

interface ProductCardProps {
  readonly product: Product;
  readonly trackingLabel?: string;
}

export const ProductCard: React.FC<ProductCardProps> = ({ product, trackingLabel }) => {
  const customUrl = useMemo(() => `${CUSTOM_URL}${product.product}`, [product.product]);

  const analyticsParams = useMemo(
    () => ({
      url: product.customUrl ?? customUrl,
      position: 'products_box',
      /* eslint-disable @typescript-eslint/naming-convention */
      original_product: DYNAMIC_PRODUCT_PROPERTIES.source[product.product],
      page_name: trackingLabel ?? '',
      /* eslint-enable @typescript-eslint/naming-convention */
    }),
    [customUrl, product.customUrl, product.product, trackingLabel],
  );

  const memoizedCta = useCallback(
    ({ size, content }: { size: 'small' | 'tiny'; content: string }) => {
      return (
        <Cta
          analyticsParams={analyticsParams}
          customUrl={product.customUrl}
          data-source={DYNAMIC_PRODUCT_PROPERTIES.source[product.product]}
          flavor="primary"
          glowing
          product={product.product}
          size={size}
          url={product.customUrl ?? customUrl}
        >
          {content}
        </Cta>
      );
    },
    [product, customUrl, analyticsParams],
  );

  return (
    <ProductBox tabIndex={0}>
      <ImageDesktop
        aria-hidden={true}
        src={DYNAMIC_PRODUCT_PROPERTIES.imageDesktop[product.product]}
        alt={product.alt ?? ''}
      />

      <ImageMobile
        aria-hidden={true}
        src={DYNAMIC_PRODUCT_PROPERTIES.imageMobile[product.product]}
        alt={product.alt ?? ''}
      />

      <CardProductTitle>{parse(product.title)}</CardProductTitle>

      <CardProductSubtitle>{parse(product.subtitle)}</CardProductSubtitle>

      <CTAContainer>
        <ButtonWrapper display={['block', 'none']}>
          {memoizedCta({ size: 'tiny', content: product.ctaLabelMobile })}
        </ButtonWrapper>
        <ButtonWrapper display={['none', 'block']}>
          {memoizedCta({ size: 'small', content: product.ctaLabelDesktop })}
        </ButtonWrapper>
      </CTAContainer>
      <PricePeriod>{product.price}</PricePeriod>
    </ProductBox>
  );
};
